import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import LandingPage from './LandingPage';
import AuthModal from './AuthModal';
import UpgradeModal from './UpgradeModal';
import ConfirmationModal from './ConfirmationModal';
import AdBanner from './AdBanner';
import { notesAPI, foldersAPI, authAPI, offlineUtils } from './apiService';

function App() {
  const [showLanding, setShowLanding] = useState(true);
  const [activeView, setActiveView] = useState('notes');
  const [folders, setFolders] = useState([]);
  const [notes, setNotes] = useState([]);
  const [noteText, setNoteText] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [enhancedNote, setEnhancedNote] = useState(null);
  const [showComparison, setShowComparison] = useState(false);
  const [expandedNotes, setExpandedNotes] = useState(new Set());
  const [generatedText, setGeneratedText] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  
  // Authentication state
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [syncStatus, setSyncStatus] = useState('synced'); // 'syncing', 'offline', 'synced'
  const [searchQuery, setSearchQuery] = useState('');
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingNoteData, setEditingNoteData] = useState({ title: '', text: '' });
  const [showStorageEditModal, setShowStorageEditModal] = useState(false);
  
  // Subscription state
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [confirmationConfig, setConfirmationConfig] = useState({});
  const [showEnhancementAd, setShowEnhancementAd] = useState(false);
  const [pendingEnhancement, setPendingEnhancement] = useState(null);
  
  const fileInputRef = useRef(null);
const videoRef = useRef(null);
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  // Anthropic API key - get one from https://console.anthropic.com/
  const ANTHROPIC_API_KEY = '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';
  const [openedFolderId, setOpenedFolderId] = useState(null);
  const [renamingFolderId, setRenamingFolderId] = useState(null);
  const [newFolderName, setNewFolderName] = useState('');
  const [draggedNote, setDraggedNote] = useState(null);
  const [dragOverFolderId, setDragOverFolderId] = useState(null);
  const [folderDragId, setFolderDragId] = useState(null);
  const [folderDragOverId, setFolderDragOverId] = useState(null);
  const [noteDropZoneActive, setNoteDropZoneActive] = useState(false);
  const [noteTitle, setNoteTitle] = useState('');
  const [openedNoteId, setOpenedNoteId] = useState(null);
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef(null);
  // No need for speechTranscript or lastResultIndexRef

  // Check for existing authentication on component mount
  useEffect(() => {
    const savedToken = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    
    if (savedToken && savedUser) {
      try {
        setToken(savedToken);
        setUser(JSON.parse(savedUser));
        // Will trigger syncDataFromCloud in the next useEffect
      } catch (error) {
        // Clear invalid data
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        loadFromLocalStorage();
      }
    } else {
      // Not authenticated, load from local storage
      loadFromLocalStorage();
    }
  }, []);

  // Sync data when user changes or comes online
  useEffect(() => {
    if (user) {
      // Skip connectivity check on initial sign-in since successful auth proves connectivity
      syncDataFromCloud(0, true);
      // Process any queued offline actions
      offlineUtils.processOfflineQueue();
    }
  }, [user]);

  // Listen for online/offline events
  useEffect(() => {
    const handleOnline = async () => {
      console.log('Browser detected online status');
      if (user) {
        // Give a moment for connection to stabilize
        setTimeout(async () => {
          const isActuallyOnline = await offlineUtils.isOnline();
          if (isActuallyOnline) {
            console.log('Confirmed online connectivity, syncing...');
            setSyncStatus('syncing');
            try {
              await syncDataFromCloud();
              await offlineUtils.processOfflineQueue();
              setSyncStatus('synced');
            } catch (error) {
              console.error('Sync failed on online event:', error);
              // Don't set offline immediately - could be temporary
              setSyncStatus('synced'); // Keep previous status for now
            }
          } else {
            console.log('False positive: still offline');
            setSyncStatus('offline');
          }
        }, 2000); // Give more time for connection to stabilize
      }
    };

    const handleOffline = () => {
      console.log('Browser detected offline status');
      setSyncStatus('offline');
    };

    // Add periodic connectivity check
    const connectivityCheck = setInterval(async () => {
      if (user && syncStatus === 'offline') {
        const isOnline = await offlineUtils.isOnline();
        if (isOnline) {
          console.log('Connectivity restored, attempting sync...');
          setSyncStatus('syncing');
          try {
            await syncDataFromCloud();
            setSyncStatus('synced');
          } catch (error) {
            console.error('Sync failed after connectivity restored:', error);
            // Don't immediately set to offline - could be temporary backend issue
            setTimeout(async () => {
              const stillOnline = await offlineUtils.isOnline();
              if (!stillOnline) {
                setSyncStatus('offline');
              }
            }, 5000);
          }
        }
      }
    }, 60000); // Check every 60 seconds (less aggressive)

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(connectivityCheck);
    };
  }, [user, syncStatus]);

  // Authentication handlers
  const handleAuthSuccess = (authData) => {
    setUser(authData.user);
    setToken(authData.token);
    
    // Store in localStorage for persistence
    localStorage.setItem('token', authData.token);
    localStorage.setItem('user', JSON.stringify(authData.user));
    
    setShowAuthModal(false);
    
    // Sync data from cloud after successful authentication
    setTimeout(() => syncDataFromCloud(), 500);
  };

  const handleLogout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    // Clear local data
    setNotes([]);
    setFolders([]);
    setSubscriptionInfo(null);
    setShowLanding(true);
  };

  const openAuthModal = () => {
    setShowAuthModal(true);
  };

  // Manual retry sync function
  const handleRetrySync = async () => {
    if (!user) return;
    
    setSyncStatus('syncing');
    setIsLoading(true);
    
    try {
      await syncDataFromCloud();
    } catch (error) {
      console.error('Manual retry failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Sync data from cloud storage
  const syncDataFromCloud = async (retryCount = 0, skipConnectivityCheck = false) => {
    if (!user) {
      setSyncStatus('offline');
      return;
    }

    // Skip connectivity check on initial sign-in since successful auth proves connectivity
    if (!skipConnectivityCheck) {
      const isOnline = await offlineUtils.isOnline();
      if (!isOnline) {
        setSyncStatus('offline');
        return;
      }
    }

    try {
      setIsLoading(true);
      setSyncStatus('syncing');
      
      // Load notes, folders, and user info from cloud
      const [cloudNotes, cloudFolders, userInfo] = await Promise.all([
        notesAPI.getAll(),
        foldersAPI.getAll(),
        authAPI.getMe()
      ]);
      
      // Normalize IDs for consistency (backend uses _id, frontend prefers id)
      const normalizedNotes = cloudNotes.map(note => ({
        ...note,
        id: note._id || note.id
      }));
      
      const normalizedFolders = cloudFolders.map(folder => ({
        ...folder,
        id: folder._id || folder.id
      }));
      
      setNotes(normalizedNotes);
      setFolders(normalizedFolders);
      setSubscriptionInfo(userInfo);
      
      // Save to local storage as backup
      offlineUtils.saveToLocal('notes', normalizedNotes);
      offlineUtils.saveToLocal('folders', normalizedFolders);
      
      setSyncStatus('synced');
      console.log('Successfully synced data from cloud');
    } catch (error) {
      console.error('Failed to sync from cloud:', error);
      
      // Check if it's a network error and we should retry
      const isNetworkError = error.message.includes('fetch') || 
                            error.message.includes('network') ||
                            error.message.includes('Failed to fetch') ||
                            error.name === 'TypeError';
      
      if (isNetworkError && retryCount < 2) {
        console.log(`Retrying sync... attempt ${retryCount + 1}/3`);
        // Wait a bit before retrying
        setTimeout(() => syncDataFromCloud(retryCount + 1, false), 2000);
        return;
      }
      
      // If this is the initial sync after sign-in, be more forgiving
      if (skipConnectivityCheck && retryCount === 0) {
        console.log('Initial sync failed, but user just signed in successfully. Loading local data and staying online.');
        loadFromLocalStorage();
        setSyncStatus('synced'); // Stay online since user just signed in
        return;
      }
      
      // Fall back to local storage
      loadFromLocalStorage();
      setSyncStatus('offline');
    } finally {
      setIsLoading(false);
    }
  };

  // Load data from local storage (fallback)
  const loadFromLocalStorage = () => {
    const savedNotes = offlineUtils.loadFromLocal('notes') || [];
    const savedFolders = offlineUtils.loadFromLocal('folders') || [];
    setNotes(savedNotes);
    setFolders(savedFolders);
  };

  // Search filtering functions
  const getFilteredNotes = () => {
    if (!searchQuery.trim()) return notes;
    
    return notes.filter(note => 
      note.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      note.text.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  const getFilteredFolders = () => {
    if (!searchQuery.trim()) return folders;
    
    return folders.filter(folder =>
      folder.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  // Note editing handlers
  const handleEditNote = (note) => {
    setEditingNoteId(note.id || note._id);
    setEditingNoteData({
      title: note.title,
      text: note.text
    });
  };

  const handleStorageEditNote = (note) => {
    setEditingNoteId(note.id || note._id);
    setEditingNoteData({
      title: note.title,
      text: note.text
    });
    setShowStorageEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!editingNoteData.title.trim() || !editingNoteData.text.trim()) {
      alert('Please fill in both title and content!');
      return;
    }

    try {
      const noteToUpdate = notes.find(n => (n.id || n._id) === editingNoteId);
      const updatedNote = {
        ...noteToUpdate,
        title: editingNoteData.title.trim(),
        text: editingNoteData.text.trim(),
        date: new Date().toLocaleString() // Update modification date
      };

      if (user && offlineUtils.isOnline()) {
        // Update in cloud
        setSyncStatus('syncing');
        const savedNote = await notesAPI.update(editingNoteId, updatedNote);
        setNotes(prevNotes => 
          prevNotes.map(note => 
            (note.id || note._id) === editingNoteId ? savedNote : note
          )
        );
        setSyncStatus('synced');
      } else {
        // Update locally
        setNotes(prevNotes => 
          prevNotes.map(note => 
            (note.id || note._id) === editingNoteId ? updatedNote : note
          )
        );
        
        if (user) {
          // Queue for sync when online
          offlineUtils.queueOfflineAction({
            type: 'UPDATE_NOTE',
            id: editingNoteId,
            data: updatedNote
          });
          setSyncStatus('offline');
        }
      }

      // Exit edit mode
      setEditingNoteId(null);
      setEditingNoteData({ title: '', text: '' });
    } catch (error) {
      console.error('Failed to update note:', error);
      alert('Failed to update note. Please try again.');
    }
  };

  const handleStorageEditSave = async () => {
    try {
      const noteId = editingNoteId;
      const updatedData = {
        title: editingNoteData.title,
        text: editingNoteData.text
      };

      if (user && token) {
        try {
          await notesAPI.update(noteId, updatedData);
          setSyncStatus('synced');
        } catch (error) {
          console.error('Failed to sync edit to cloud:', error);
          offlineUtils.queueOfflineAction('update', 'notes', noteId, updatedData);
          setSyncStatus('offline');
        }
      }

      setNotes(prevNotes => 
        prevNotes.map(note => 
          (note.id || note._id) === noteId 
            ? { ...note, ...updatedData }
            : note
        )
      );

      offlineUtils.saveToLocal('notes', notes.map(note => 
        (note.id || note._id) === noteId 
          ? { ...note, ...updatedData }
          : note
      ));

      setShowStorageEditModal(false);
      setEditingNoteId(null);
      setEditingNoteData({ title: '', text: '' });
    } catch (error) {
      console.error('Failed to save edit:', error);
    }
  };

  const handleStorageEditCancel = () => {
    setShowStorageEditModal(false);
    setEditingNoteId(null);
    setEditingNoteData({ title: '', text: '' });
  };

  const handleCancelEdit = () => {
    setEditingNoteId(null);
    setEditingNoteData({ title: '', text: '' });
  };

  // Update saveNote to use cloud storage for authenticated users
  const saveNote = async () => {
    if (!noteText.trim() && !noteTitle.trim()) return;

    // Check usage limits for free users
    if (subscriptionInfo && !subscriptionInfo.isPro) {
      if (subscriptionInfo.usage.notesThisMonth >= subscriptionInfo.usage.notesLimit) {
        setShowUpgradeModal(true);
        return;
      }
    }

    const newNote = {
      title: noteTitle.trim() || 'Untitled Note',
      text: noteText,
      date: new Date().toLocaleString(),
      type: 'text',
      isCollapsible: true,
      isExpanded: false
    };

    // Cloud-first approach
    if (user && token) {
      try {
        const savedNote = await notesAPI.create(newNote);
        const normalizedNote = {
          ...savedNote,
          id: savedNote._id || savedNote.id
        };
        setNotes(prevNotes => [normalizedNote, ...prevNotes]);
        
        // Update subscription info after successful note creation
        const updatedUserInfo = await authAPI.getMe();
        setSubscriptionInfo(updatedUserInfo);
        
        setSyncStatus('synced');
      } catch (error) {
        console.error('Failed to save note to cloud:', error);
        
        // Check if it's a usage limit error
        if (error.message.includes('limit reached') || error.message.includes('Monthly note limit')) {
          setShowUpgradeModal(true);
          return;
        }
        
        // Check if it's a backend connectivity issue
        if (error.message.includes('Cannot connect to backend server') || 
            error.message.includes('Backend server is not responding')) {
          // Show a user-friendly message but don't change sync status immediately
          console.warn('Backend server unavailable, saving locally only');
          // Only set offline if we weren't already synced recently
          if (syncStatus !== 'synced') {
            setSyncStatus('offline');
          }
        } else {
          setSyncStatus('offline');
        }
        
        // Queue for offline sync
        offlineUtils.queueOfflineAction('create', 'notes', null, newNote);
        setNotes(prevNotes => [newNote, ...prevNotes]);
      }
    } else {
      // Offline mode
      setNotes(prevNotes => [newNote, ...prevNotes]);
    }

    // Local storage backup
    const updatedNotes = [newNote, ...notes];
    offlineUtils.saveToLocal('notes', updatedNotes);

    setNoteText('');
    setNoteTitle('');
  };

  const deleteNote = async (id) => {
    // Find the note to delete
    const noteToDelete = notes.find(note => (note.id || note._id) === id);
    if (!noteToDelete) return;
    
    // Remove from local state
    const updatedNotes = notes.filter(note => (note.id || note._id) !== id);
    setNotes(updatedNotes);
    
    // Save to cloud if user is logged in
    if (user && token) {
      try {
        await notesAPI.delete(id);
        setSyncStatus('synced');
      } catch (error) {
        console.error('Failed to sync note deletion to cloud:', error);
        offlineUtils.queueOfflineAction('delete', 'notes', id, null);
        setSyncStatus('offline');
      }
    }
    
    // Save to local storage
    offlineUtils.saveToLocal('notes', updatedNotes);
  };

  const handleKeyDown = (e) => {
    if (e.ctrlKey && e.key === 'Enter') {
      saveNote();
    }
  };

  // Camera capture handler
const handleCameraCapture = async () => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ video: true });
    setIsCameraActive(true);
    setCameraStream(stream);
    if (videoRef.current) {
      videoRef.current.srcObject = stream;
    }
  } catch (error) {
    alert('Error accessing camera: ' + error.message);
  }
};

const capturePhoto = async () => {
  if (!videoRef.current || !cameraStream) return;

  const canvas = document.createElement('canvas');
  canvas.width = videoRef.current.videoWidth;
  canvas.height = videoRef.current.videoHeight;
  canvas.getContext('2d').drawImage(videoRef.current, 0, 0);
  
  canvas.toBlob(async (blob) => {
    const file = new File([blob], `camera-photo-${Date.now()}.jpeg`, { 
      type: 'image/jpeg' 
    });
    await handleFileUpload([file]);
    
    // Cleanup camera
    cameraStream.getTracks().forEach(track => track.stop());
    setIsCameraActive(false);
    setCameraStream(null);
  }, 'image/jpeg', 0.9);
};

const handleFileUpload = async (files) => {
    setIsUploading(true);
    setUploadProgress(0);

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      // Validate file type
      const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
      if (!validTypes.includes(file.type)) {
        alert(`File type ${file.type} is not supported. Please upload images (JPEG, PNG, GIF, WebP) or PDFs.`);
        continue;
      }

      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        alert(`File ${file.name} is too large. Maximum size is 10MB.`);
        continue;
      }

      try {
        // Simulate upload progress
        for (let progress = 0; progress <= 100; progress += 10) {
          setUploadProgress(progress);
          await new Promise(resolve => setTimeout(resolve, 50));
        }

        // Convert file to base64 for storage
        const base64 = await fileToBase64(file);
        
        const newNote = {
          text: `📎 ${file.name}`,
          date: new Date().toLocaleString(),
          type: 'file',
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
          fileData: base64
        };

        setNotes(prevNotes => [newNote, ...prevNotes]);
      } catch (error) {
        console.error('Error uploading file:', error);
        alert(`Error uploading ${file.name}: ${error.message}`);
      }
    }

    setIsUploading(false);
    setUploadProgress(0);
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    handleFileUpload(files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const openFileInput = () => {
    fileInputRef.current.click();
  };

  const handleFileInputChange = (e) => {
    const files = Array.from(e.target.files);
    handleFileUpload(files);
    e.target.value = ''; // Reset input
  };

  const openFile = (note) => {
    if (note.type === 'file') {
      const link = document.createElement('a');
      link.href = note.fileData;
      link.download = note.fileName;
      link.click();
    }
  };

  const toggleNoteExpansion = (noteId) => {
    setExpandedNotes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(noteId)) {
        newSet.delete(noteId);
      } else {
        newSet.add(noteId);
      }
      return newSet;
    });
  };

  // Function to convert markdown to HTML
  const markdownToHtml = (text) => {
    return text
      // Headings
      .replace(/^### (.*$)/gim, '<h3>$1</h3>')
      .replace(/^## (.*$)/gim, '<h2>$1</h2>')
      .replace(/^# (.*$)/gim, '<h1>$1</h1>')
      .replace(/^#### (.*$)/gim, '<h4>$1</h4>')
      .replace(/^##### (.*$)/gim, '<h5>$1</h5>')
      .replace(/^###### (.*$)/gim, '<h6>$1</h6>')
      
      // Bold and italic
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/__(.*?)__/g, '<strong>$1</strong>')
      .replace(/_(.*?)_/g, '<em>$1</em>')
      
      // Underlines
      .replace(/<u>(.*?)<\/u>/g, '<u>$1</u>')
      .replace(/___(.*?)___/g, '<u>$1</u>')
      
      // Highlighting
      .replace(/==(.*?)==/g, '<mark>$1</mark>')
      .replace(/<mark>(.*?)<\/mark>/g, '<mark>$1</mark>')
      
      // Special highlighting classes
      .replace(/\[IMPORTANT\](.*?)\[\/IMPORTANT\]/g, '<span class="important">$1</span>')
      .replace(/\[WARNING\](.*?)\[\/WARNING\]/g, '<span class="warning">$1</span>')
      .replace(/\[INFO\](.*?)\[\/INFO\]/g, '<span class="info">$1</span>')
      .replace(/\[KEY\](.*?)\[\/KEY\]/g, '<span class="key-term">$1</span>')
      .replace(/\[DEF\](.*?)\[\/DEF\]/g, '<div class="definition">$1</div>')
      .replace(/\[EXAMPLE\](.*?)\[\/EXAMPLE\]/g, '<div class="example">$1</div>')
      .replace(/\[TIP\](.*?)\[\/TIP\]/g, '<div class="tip">$1</div>')
      .replace(/\[STEP\](.*?)\[\/STEP\]/g, '<div class="step">$1</div>')
      
      // Line breaks
      .replace(/\n/g, '<br/>')
      
      // Lists
      .replace(/^\* (.*$)/gim, '<li>$1</li>')
      .replace(/^- (.*$)/gim, '<li>$1</li>')
      .replace(/^\d+\. (.*$)/gim, '<li>$1</li>');
  };

  const enhanceNoteWithAI = async (note) => {
    if (!ANTHROPIC_API_KEY || ANTHROPIC_API_KEY === 'your_anthropic_api_key_here') {
      alert('Please add your Anthropic API key to the ANTHROPIC_API_KEY variable in App.js');
      return;
    }

    // Show ad for free users before enhancement
    if (!subscriptionInfo?.isPro) {
      setPendingEnhancement(note);
      setShowEnhancementAd(true);
      return;
    }

    // Pro users get direct enhancement
    performEnhancement(note);
  };

  const handleAdComplete = () => {
    setShowEnhancementAd(false);
    if (pendingEnhancement) {
      // Small delay for better UX
      setTimeout(() => {
        performEnhancement(pendingEnhancement);
        setPendingEnhancement(null);
      }, 500);
    }
  };

  const handleUpgradeFromAd = () => {
    setShowEnhancementAd(false);
    setPendingEnhancement(null);
    setShowUpgradeModal(true);
  };

  const performEnhancement = async (note) => {
    setIsEnhancing(true);
    setIsStreaming(true);
    setGeneratedText('');
    setEnhancedNote(null);
    setShowComparison(false);

    try {
      let messages = [];

      if (note.type === 'file') {
        // Handle image/file enhancement
        if (note.fileType.startsWith('image/')) {
          messages = [
            {
              role: 'user',
              content: [
                {
                  type: 'text',
                  text: 'Analyze this image and create a comprehensive note. Use markdown formatting with different heading levels (# for H1, ## for H2, ### for H3), **bold** for emphasis, __underlines__, ==highlighting== for important info, and [IMPORTANT]text[/IMPORTANT] for special highlighting. Organize with bullet points and numbered lists. Use emojis for visual appeal.'
                },
                {
                  type: 'image',
                  source: {
                    type: 'base64',
                    media_type: 'image/jpeg',
                    data: note.fileData.split(',')[1] // Extract base64 without data URL prefix
                  }
                }
              ]
            }
          ];
        } else {
          // For PDFs, we'd need to extract text first
          messages = [
            {
              role: 'user',
              content: `Analyze this PDF and create a comprehensive summary. Use markdown formatting with different heading levels (# for H1, ## for H2, ### for H3), **bold** for emphasis, __underlines__, ==highlighting== for important info, and [IMPORTANT]text[/IMPORTANT] for special highlighting. Organize with bullet points and numbered lists. Use emojis for visual appeal. File: ${note.fileName}`
            }
          ];
        }
      } else {
        // Handle text note enhancement
        messages = [
          {
            role: 'user',
            content: `Enhance this note with rich markdown formatting including different heading levels (# for H1, ## for H2, ### for H3), **bold** for emphasis, __underlines__, ==highlighting== for important info, and special highlighting like [IMPORTANT]text[/IMPORTANT], [WARNING]text[/WARNING], [INFO]text[/INFO], [KEY]terms[/KEY], [DEF]definitions[/DEF], [EXAMPLE]examples[/EXAMPLE], [TIP]tips[/TIP], [STEP]steps[/STEP]. Use bullet points and numbered lists. Add emojis for visual appeal.

Format your response with:
- # H1 headings (largest) for main topics
- ## H2 headings for major sections  
- ### H3 headings for subsections
- **bold text** for emphasis
- __underlined text__ for key terms
- ==highlighted text== for important information
- [IMPORTANT]critical information[/IMPORTANT]
- [WARNING]important warnings[/WARNING]
- [INFO]useful information[/INFO]
- [KEY]key terms[/KEY]
- [DEF]definitions[/DEF]
- [EXAMPLE]examples[/EXAMPLE]
- [TIP]helpful tips[/TIP]
- [STEP]step-by-step instructions[/STEP]

Original: "${note.text}"`
          }
        ];
      }

      const requestBody = {
        model: 'claude-3-haiku-20240307',
        messages: messages,
        max_tokens: 1500,
        temperature: 0.7,
        stream: true // Enable streaming
      };

      const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': ANTHROPIC_API_KEY,
          'anthropic-version': '2023-06-01',
          'anthropic-dangerous-direct-browser-access': 'true'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorText = await response.text();
        
        let errorMessage = `API request failed: ${response.status}`;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage += ` - ${errorData.error?.message || errorData.message || response.statusText}`;
        } catch (e) {
          errorMessage += ` - ${errorText}`;
        }
        
        throw new Error(errorMessage);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let fullText = '';

      while (true) {
        const { done, value } = await reader.read();
        
        if (done) break;
        
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data === '[DONE]') {
              setIsStreaming(false);
              const enhancedHtml = markdownToHtml(fullText);
              setEnhancedNote({
                original: note,
                enhanced: enhancedHtml,
                timestamp: new Date().toLocaleString()
              });
              setShowComparison(true);
              return;
            }

            try {
              const parsed = JSON.parse(data);
              if (parsed.type === 'content_block_delta' && parsed.delta && parsed.delta.text) {
                const content = parsed.delta.text;
                fullText += content;
                const enhancedHtml = markdownToHtml(fullText);
                setGeneratedText(enhancedHtml);
              }
            } catch (e) {
              // Skip invalid JSON
            }
          }
        }
      }

      // Fallback: if we reach here, streaming completed but didn't get [DONE]
      setIsStreaming(false);
      const enhancedHtml = markdownToHtml(fullText);
      setEnhancedNote({
        original: note,
        enhanced: enhancedHtml,
        timestamp: new Date().toLocaleString()
      });
      setShowComparison(true);

    } catch (error) {
      console.error('Error enhancing note:', error);
      alert(`Error enhancing note: ${error.message}`);
      setIsStreaming(false);
    } finally {
      setIsEnhancing(false);
    }
  };

  const saveEnhancedNote = async () => {
    if (!enhancedNote) return;

    const newNote = {
      title: enhancedNote.original.title,
      text: enhancedNote.enhanced,
      date: new Date().toLocaleString(),
      type: 'text',
      isEnhanced: true,
      originalNoteId: enhancedNote.original.id || enhancedNote.original._id,
      originalText: enhancedNote.original.text,
      isCollapsible: true,
      isExpanded: false
    };

    // Save enhanced note to cloud if user is authenticated
    if (user && token) {
      try {
        const savedNote = await notesAPI.create(newNote);
        const normalizedNote = {
          ...savedNote,
          id: savedNote._id || savedNote.id
        };
        setNotes(prevNotes => [normalizedNote, ...prevNotes]);
        console.log('Enhanced note saved to cloud with ID:', normalizedNote.id);
      } catch (error) {
        console.error('Failed to save enhanced note to cloud:', error);
        // Fallback to local storage with temporary ID
        const localNote = {
          ...newNote,
          id: 'enhanced_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9)
        };
        setNotes(prevNotes => [localNote, ...prevNotes]);
      }
    } else {
      // For offline users, generate temporary ID
      const localNote = {
        ...newNote,
        id: 'enhanced_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9)
      };
      setNotes(prevNotes => [localNote, ...prevNotes]);
    }

    setEnhancedNote(null);
    setShowComparison(false);
    setGeneratedText('');
  };

  const renderNoteContent = (note) => {
    const isExpanded = expandedNotes.has(note.id);
    
    // Helper function to get clean text preview (without HTML tags)
    const getCleanTextPreview = (text) => {
      // Remove HTML tags and get clean text
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = text;
      return tempDiv.textContent || tempDiv.innerText || '';
    };
    
    return (
      <div className="collapsible-note">
        <div className="note-header" onClick={() => toggleNoteExpansion(note.id)}>
          <div className="expand-icon">
            {isExpanded ? '▼' : '▶'}
          </div>
          <div className="note-summary">
            <strong>{note.title || 'Untitled Note'}</strong>
            <span className="note-preview">
              {getCleanTextPreview(note.text).substring(0, 100)}...
            </span>
          </div>
        </div>
        
        {isExpanded && (
          <div className="note-content-expanded">
            <div className="note-text" dangerouslySetInnerHTML={{ __html: note.text.replace(/\n/g, '<br/>') }} />
          </div>
        )}
      </div>
    );
  };

  // Handler to open a folder
  const handleOpenFolder = (folderId) => {
    setOpenedFolderId(folderId);
  };

  // Handler for folder card clicks (prevents conflicts with drag operations)
  const handleFolderCardClick = (e, folderId) => {
    // Only open folder if it's a direct click, not a drag operation
    if (e.detail === 1 && !draggedNote) {
      setTimeout(() => {
        if (!draggedNote) {
          handleOpenFolder(folderId);
        }
      }, 100);
    }
  };

  // Handler to start renaming a folder
  const handleRenameFolder = (folderId, currentName) => {
    setRenamingFolderId(folderId);
    setNewFolderName(currentName);
  };

  // Handler to save folder name
  const handleSaveFolderName = async (folderId) => {
    if (!newFolderName.trim()) {
      // Keep in renaming mode if name is empty
      return;
    }
    
    const updatedFolders = folders.map(f => f.id === folderId ? { ...f, name: newFolderName.trim() || f.name } : f);
    setFolders(updatedFolders);
    
    // Save to cloud if user is logged in
    if (user && token) {
      try {
        const folderToUpdate = updatedFolders.find(f => f.id === folderId);
        if (folderToUpdate) {
          // Check if this is a temp folder (not yet synced to backend)
          if (folderId.startsWith('temp-folder-')) {
            // This is a temp folder, queue the name change for when it gets created
            offlineUtils.queueOfflineAction('create', 'folders', null, { name: folderToUpdate.name });
            setSyncStatus('offline');
          } else {
            // This is a real folder from backend, update it
            await foldersAPI.update(folderId, { name: folderToUpdate.name });
            setSyncStatus('synced');
          }
        }
      } catch (error) {
        console.error('Failed to sync folder rename to cloud:', error);
        offlineUtils.queueOfflineAction('update', 'folders', folderId, { name: newFolderName.trim() });
        setSyncStatus('offline');
      }
    }
    
    // Save to local storage
    offlineUtils.saveToLocal('folders', updatedFolders);
    
    setRenamingFolderId(null);
    setNewFolderName('');
  };

  // Handler to create a new folder
  const handleCreateFolder = async () => {
    // Check usage limits for free users
    if (subscriptionInfo && !subscriptionInfo.isPro) {
      if (subscriptionInfo.usage.foldersThisMonth >= subscriptionInfo.usage.foldersLimit) {
        setShowUpgradeModal(true);
        return;
      }
    }

    const tempId = `temp-folder-${Date.now()}`;
    const name = `New Folder`;
    
    // Create temporary folder for immediate UI feedback
    const tempFolder = { id: tempId, name, created: new Date().toISOString() };
    setFolders([tempFolder, ...folders]);
    
    // Save to cloud if user is logged in
    if (user && token) {
      try {
        // Send only the data the backend expects
        const createdFolder = await foldersAPI.create({ name });
        
        // Replace temp folder with the real one from backend
        setFolders(prevFolders => [
          { ...createdFolder, id: createdFolder._id || createdFolder.id }, 
          ...prevFolders.filter(f => f.id !== tempId)
        ]);
        
        // Update subscription info after successful folder creation
        const updatedUserInfo = await authAPI.getMe();
        setSubscriptionInfo(updatedUserInfo);
        
        // Update opened/renaming IDs to use the new real ID
        const realId = createdFolder._id || createdFolder.id;
        setOpenedFolderId(realId);
        setRenamingFolderId(realId);
        
        setSyncStatus('synced');
      } catch (error) {
        console.error('Failed to sync folder creation to cloud:', error);
        
        // Check if it's a usage limit error
        if (error.message.includes('limit reached') || error.message.includes('Folder limit')) {
          setShowUpgradeModal(true);
          // Remove the temp folder since creation failed
          setFolders(prevFolders => prevFolders.filter(f => f.id !== tempId));
          return;
        }
        
        offlineUtils.queueOfflineAction('create', 'folders', null, { name });
        setSyncStatus('offline');
        
        // Keep temp folder for offline mode
        setOpenedFolderId(tempId);
        setRenamingFolderId(tempId);
      }
    } else {
      // Offline mode - keep temp folder
      setOpenedFolderId(tempId);
      setRenamingFolderId(tempId);
    }
    
    // Save current state to local storage
    offlineUtils.saveToLocal('folders', [tempFolder, ...folders]);
    setNewFolderName(name);
  };

  // Handler to close folder view
  const handleCloseFolder = () => {
    setOpenedFolderId(null);
  };

  // Handler to open a note
  const handleOpenNote = (noteId) => {
    setOpenedNoteId(noteId);
  };

  // Handler to close note view
  const handleCloseNote = () => {
    setOpenedNoteId(null);
  };

  // Helper function to get clean text preview (without HTML tags)
  const getCleanTextPreview = (text) => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = text;
    return tempDiv.textContent || tempDiv.innerText || '';
  };

  // Drag handlers for notes
  const handleNoteDragStart = (note, fromFolderId = null) => {
    // Use the note's actual folderId instead of the parameter for accuracy
    const actualFromFolderId = note.folderId || null;
    setDraggedNote({ ...note, fromFolderId: actualFromFolderId });
  };
  const handleNoteDragEnd = () => {
    setDraggedNote(null);
  };

  // Drag handlers for folders
  const handleFolderDragOver = (e, folderId) => {
    e.preventDefault();
    setDragOverFolderId(folderId);
  };
  const handleFolderDragLeave = () => {
    setDragOverFolderId(null);
  };
  const handleFolderDrop = async (e, folderId) => {
    e.preventDefault();
    setDragOverFolderId(null);
    if (!draggedNote) return;
    
    console.log('Dropping note into folder:', { noteId: draggedNote.id || draggedNote._id, folderId, fromFolderId: draggedNote.fromFolderId });
    
    // Skip if note is already in this folder
    if (draggedNote.folderId === folderId) {
      setDraggedNote(null);
      return;
    }
    
    // Update the note's folderId
    const noteId = draggedNote.id || draggedNote._id;
    const updatedNotes = notes.map(note => 
      (note.id || note._id) === noteId
        ? { ...note, folderId: folderId }
        : note
    );
    setNotes(updatedNotes);
    
    // Save to cloud if user is logged in
    if (user && token) {
      try {
        await notesAPI.update(noteId, { folderId: folderId });
        setSyncStatus('synced');
      } catch (error) {
        console.error('Failed to sync note move to cloud:', error);
        offlineUtils.queueOfflineAction('update', 'notes', noteId, { folderId: folderId });
        setSyncStatus('offline');
      }
    }
    
    // Save to local storage
    offlineUtils.saveToLocal('notes', updatedNotes);
    
    setDraggedNote(null);
  };

  // Delete folder handler
  const handleDeleteFolder = async (folderId) => {
    if (folderId === 'default') return;
    if (window.confirm('Are you sure you want to delete this folder and all its notes?')) {
      // Remove the folder
      const updatedFolders = folders.filter(f => f.id !== folderId);
      setFolders(updatedFolders);
      
      // Move all notes from this folder back to no folder
      const updatedNotes = notes.map(note => 
        note.folderId === folderId 
          ? { ...note, folderId: null }
          : note
      );
      setNotes(updatedNotes);
      
      // Save to cloud if user is logged in
      if (user && token) {
        try {
          await foldersAPI.delete(folderId);
          // Also update notes in cloud
          const notesToUpdate = notes.filter(note => note.folderId === folderId);
          for (const note of notesToUpdate) {
            try {
              await notesAPI.update(note.id || note._id, { folderId: null });
            } catch (error) {
              console.error('Failed to update note in cloud:', error);
            }
          }
          setSyncStatus('synced');
        } catch (error) {
          console.error('Failed to sync folder deletion to cloud:', error);
          offlineUtils.queueOfflineAction('delete', 'folders', folderId, null);
          setSyncStatus('offline');
        }
      }
      
      // Save to local storage
      offlineUtils.saveToLocal('folders', updatedFolders);
      offlineUtils.saveToLocal('notes', updatedNotes);
      
      if (openedFolderId === folderId) setOpenedFolderId(null);
    }
  };

  // Drag and drop for moving notes out of folders
  const handleStorageHomeDragOver = (e) => {
    e.preventDefault();
    setNoteDropZoneActive(true);
  };
  const handleStorageHomeDragLeave = () => {
    setNoteDropZoneActive(false);
  };
  const handleStorageHomeDrop = async (e) => {
    e.preventDefault();
    setNoteDropZoneActive(false);
    if (!draggedNote || !draggedNote.fromFolderId) return;
    
    console.log('Moving note out of folder:', { noteId: draggedNote.id || draggedNote._id, fromFolderId: draggedNote.fromFolderId });
    
    // Skip if note is already outside folders
    if (!draggedNote.folderId) {
      setDraggedNote(null);
      return;
    }
    
    // Remove folderId from the note (move it out of folder)
    const noteId = draggedNote.id || draggedNote._id;
    const updatedNotes = notes.map(note => 
      (note.id || note._id) === noteId
        ? { ...note, folderId: null }
        : note
    );
    setNotes(updatedNotes);
    
    // Save to cloud if user is logged in
    if (user && token) {
      try {
        await notesAPI.update(noteId, { folderId: null });
        setSyncStatus('synced');
      } catch (error) {
        console.error('Failed to sync note move to cloud:', error);
        offlineUtils.queueOfflineAction('update', 'notes', noteId, { folderId: null });
        setSyncStatus('offline');
      }
    }
    
    // Save to local storage
    offlineUtils.saveToLocal('notes', updatedNotes);
    
    setDraggedNote(null);
  };

  // Drag and drop for reordering folders
  const handleFolderDragStart = (folderId) => {
    setFolderDragId(folderId);
  };
  const handleFolderDragEnd = () => {
    setFolderDragId(null);
    setFolderDragOverId(null);
  };
  const handleFolderCardDragOver = (e, overId) => {
    e.preventDefault();
    setFolderDragOverId(overId);
  };
  const handleFolderCardDrop = (e, overId) => {
    e.preventDefault();
    if (!folderDragId || folderDragId === overId) return;
    const oldIndex = folders.findIndex(f => f.id === folderDragId);
    const newIndex = folders.findIndex(f => f.id === overId);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = [...folders];
    const [moved] = reordered.splice(oldIndex, 1);
    reordered.splice(newIndex, 0, moved);
    setFolders(reordered);
    setFolderDragId(null);
    setFolderDragOverId(null);
  };

  // Helper to get notes not in any folder (with search filtering)
  const filteredNotes = getFilteredNotes();
  const filteredFolders = getFilteredFolders();
  const notesNotInFolders = filteredNotes.filter(note => !note.folderId);
  
  // Helper to get the 10 most recent notes (by date, with search filtering)
  const sortedNotes = [...filteredNotes].sort((a, b) => new Date(b.date) - new Date(a.date));
  const recentNotes = sortedNotes.slice(0, 10);

  // Handler to cancel renaming
  const handleCancelRename = () => {
    setRenamingFolderId(null);
    setNewFolderName('');
  };

  // Speech-to-text handlers
  const handleSpeechClick = () => {
    if (!('webkitSpeechRecognition' in window || 'SpeechRecognition' in window)) {
      alert('Speech recognition is not supported in this browser.');
      return;
    }
    if (isListening) {
      recognitionRef.current && recognitionRef.current.stop();
      setIsListening(false);
      return;
    }
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.lang = 'en-US';
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;
    if ('continuous' in recognition) recognition.continuous = true;
    recognition.onresult = (event) => {
      let fullTranscript = '';
      for (let i = 0; i < event.results.length; ++i) {
        fullTranscript += event.results[i][0].transcript;
      }
      setNoteText(fullTranscript);
    };
    recognition.onerror = (event) => {
      alert('Speech recognition error: ' + event.error);
      setIsListening(false);
    };
    recognition.onend = () => {
      // If still listening, restart recognition for continuous mode
      if (isListening) {
        recognition.start();
      } else {
        setIsListening(false);
      }
    };
    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
  };

  const handleGetStarted = (uploadedFiles = null) => {
    setShowLanding(false);
    // If files were uploaded from landing page, process them
    if (uploadedFiles && uploadedFiles.length > 0) {
      // Set active view to notes so user can see the uploaded files
      setActiveView('notes');
      // Process the uploaded files
      setTimeout(() => {
        handleFileUpload(uploadedFiles);
      }, 100); // Small delay to ensure the main app is rendered
    }
  };

  const handleBackToLanding = () => {
    setShowLanding(true);
  };

  const handleStartProTrial = () => {
    // Don't show upgrade modal if user is already Pro
    if (subscriptionInfo?.isPro) {
      return;
    }

    // If user isn't logged in, provide default usage info for the modal
    if (!user && !subscriptionInfo) {
      setSubscriptionInfo({
        usage: {
          notesThisMonth: 0,
          notesLimit: 20,
          foldersUsed: 0,
        foldersThisMonth: 0,
          foldersLimit: 5
        }
      });
    }
    setShowUpgradeModal(true);
  };

  // Subscription management functions
  const cancelSubscription = () => {
    setConfirmationConfig({
      title: "Cancel Subscription",
      message: "Are you sure you want to cancel your subscription? You will retain access until the end of your current billing period.",
      confirmText: "Cancel Subscription",
      cancelText: "Keep Subscription", 
      confirmButtonClass: "confirm-btn",
      onConfirm: handleCancelConfirm
    });
    setShowConfirmationModal(true);
  };

  const handleCancelConfirm = async () => {
    setShowConfirmationModal(false);
    
    console.log('Attempting to cancel subscription...');
    console.log('Auth token:', localStorage.getItem('token') ? 'Present' : 'Missing');

    try {
      const response = await fetch('/api/subscription/cancel', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      console.log('Cancel response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Cancel success:', data);
        
        // Show appropriate message based on response
        if (data.status === 'scheduled_for_cancellation') {
          alert(`${data.message}\n\nYour Pro benefits will continue until: ${new Date(data.cancelAt).toLocaleDateString()}`);
        } else {
          alert(data.message || 'Subscription canceled successfully');
        }
        
        // Refresh subscription info
        syncDataFromCloud();
      } else {
        const errorData = await response.json();
        console.error('Cancel error response:', errorData);
        
        // Handle specific error cases with better messaging
        if (errorData.error === 'No active Pro subscription found') {
          alert('You don\'t have an active Pro subscription to cancel.');
        } else if (errorData.error === 'Subscription is already scheduled for cancellation') {
          alert(`Your subscription is already scheduled for cancellation on ${new Date(errorData.cancelAt).toLocaleDateString()}.`);
        } else if (errorData.error === 'Subscription was already canceled') {
          alert(errorData.message || 'Your subscription was already canceled.');
        } else if (errorData.error === 'Subscription not found in billing system') {
          alert(errorData.message || 'Subscription not found. Your account has been updated.');
        } else {
          alert(`Failed to cancel subscription: ${errorData.error || 'Unknown error'}`);
        }
        
        // Still refresh to get updated subscription info
        syncDataFromCloud();
      }
    } catch (error) {
      console.error('Network error canceling subscription:', error);
      alert('Failed to cancel subscription. Please check your connection and try again.');
    }
  };

  const reactivateSubscription = () => {
    setConfirmationConfig({
      title: "Reactivate Subscription", 
      message: "Would you like to reactivate your subscription and continue receiving Pro benefits?",
      confirmText: "Reactivate",
      cancelText: "Cancel",
      confirmButtonClass: "reactivate-btn",
      onConfirm: handleReactivateConfirm
    });
    setShowConfirmationModal(true);
  };

  const handleReactivateConfirm = async () => {
    setShowConfirmationModal(false);
    
    console.log('Attempting to reactivate subscription...');

    try {
      const response = await fetch('/api/subscription/reactivate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      console.log('Reactivate response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('Reactivate success:', data);
        
        // Show appropriate message based on response
        if (data.status === 'active' && data.nextBillingDate) {
          alert(`${data.message}\n\nYour next billing date: ${new Date(data.nextBillingDate).toLocaleDateString()}`);
        } else {
          alert(data.message || 'Subscription reactivated successfully');
        }
        
        // Refresh subscription info
        syncDataFromCloud();
      } else {
        const errorData = await response.json();
        console.error('Reactivate error response:', errorData);
        
        // Handle specific error cases with better messaging
        if (errorData.error === 'No canceled subscription found to reactivate') {
          alert('You don\'t have a canceled subscription to reactivate.');
        } else if (errorData.error === 'Cannot reactivate canceled subscription') {
          alert(errorData.message || 'This subscription cannot be reactivated. Please start a new subscription.');
        } else if (errorData.error === 'Subscription not found in billing system') {
          alert(errorData.message || 'Subscription not found. Please start a new subscription.');
        } else {
          alert(`Failed to reactivate subscription: ${errorData.error || 'Unknown error'}`);
        }
        
        // Still refresh to get updated subscription info
        syncDataFromCloud();
      }
    } catch (error) {
      console.error('Network error reactivating subscription:', error);
      alert('Failed to reactivate subscription. Please check your connection and try again.');
    }
  };

  // Attach functions to window for access from LandingPage
  React.useEffect(() => {
    window.cancelSubscription = cancelSubscription;
    window.reactivateSubscription = reactivateSubscription;
    
    return () => {
      delete window.cancelSubscription;
      delete window.reactivateSubscription;
    };
  }, []);

  // Auto-close enhancement ad after 8 seconds
  React.useEffect(() => {
    if (showEnhancementAd) {
      const timer = setTimeout(handleAdComplete, 8000);
      return () => clearTimeout(timer);
    }
  }, [showEnhancementAd]);



  // Show landing page first
  if (showLanding) {
    return (
      <>
        <LandingPage 
          onGetStarted={handleGetStarted} 
          onSignIn={openAuthModal} 
          user={user}
          onLogout={handleLogout}
          onStartProTrial={handleStartProTrial}
          subscriptionInfo={subscriptionInfo}
        />
        {/* Auth Modal */}
        <AuthModal 
          isOpen={showAuthModal} 
          onClose={() => setShowAuthModal(false)} 
          onAuthSuccess={handleAuthSuccess} 
        />
        
        {/* Upgrade Modal - also available on landing page */}
        <UpgradeModal 
          isOpen={showUpgradeModal} 
          onClose={() => setShowUpgradeModal(false)} 
          subscriptionInfo={subscriptionInfo}
          user={user}
          onRefreshSubscription={syncDataFromCloud}
        />

        {/* Confirmation Modal - also available on landing page */}
        <ConfirmationModal
          isOpen={showConfirmationModal}
          onClose={() => setShowConfirmationModal(false)}
          title={confirmationConfig.title}
          message={confirmationConfig.message}
          confirmText={confirmationConfig.confirmText}
          cancelText={confirmationConfig.cancelText}
          confirmButtonClass={confirmationConfig.confirmButtonClass}
          onConfirm={confirmationConfig.onConfirm}
        />
      </>
    );
  }

  // Show main app
  return (
    <div className="App">
      <div className="container">
        <header className="header">
          <h1>Swift Notes</h1>
          <p>AI-powered note-taking made simple</p>
        </header>

        <div className="nav-bar">
          <button 
            className="dashboard-btn"
            onClick={handleBackToLanding}
            title="Back to Landing"
          >
            🏠 Dashboard
          </button>
          <div className="nav-buttons">
            <button 
              className={`nav-btn ${activeView === 'notes' ? 'active' : ''}`}
              onClick={() => setActiveView('notes')}
            >
              📝 Notes
            </button>
            <button 
              className={`nav-btn ${activeView === 'storage' ? 'active' : ''}`}
              onClick={() => setActiveView('storage')}
            >
              💾 Storage
            </button>
          </div>
          <div className="auth-section">
            {user ? (
              <div className="user-info">
                <div className="sync-status">
                  {isLoading && <span className="sync-indicator syncing">⟳ Syncing...</span>}
                  {!isLoading && syncStatus === 'synced' && <span className="sync-indicator synced">✓ Synced</span>}
                  {!isLoading && syncStatus === 'offline' && (
                    <div className="offline-status">
                      <span className="sync-indicator offline">⚠ Offline</span>
                      <button className="retry-sync-btn" onClick={handleRetrySync} title="Retry sync">
                        🔄
                      </button>
                    </div>
                  )}
                </div>
                <div className="user-info-section">
                  <span className="user-email">{user.email}</span>
                  {subscriptionInfo?.isPro && <span className="pro-badge">PRO</span>}
                </div>
                <button className="logout-btn" onClick={handleLogout}>Logout</button>
              </div>
            ) : (
              <button className="auth-btn" onClick={openAuthModal}>Sign In</button>
            )}
          </div>
        </div>
        <div className="content">
          {/* Search Bar */}
          <div className="search-container">
            <div className="search-box">
              <span className="search-icon">🔍</span>
              <input
                type="text"
                placeholder="Search notes and folders..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
              {searchQuery && (
                <button 
                  className="clear-search"
                  onClick={() => setSearchQuery('')}
                  title="Clear search"
                >
                  ✕
                </button>
              )}
            </div>
            {searchQuery && (
              <div className="search-results-info">
                Showing results for "{searchQuery}"
              </div>
            )}
          </div>
          {activeView === 'notes' && (
            <>
              <div className="note-form">
                <input
                  type="text"
                  className="note-title-input"
                  placeholder="Title"
                  value={noteTitle}
                  onChange={e => setNoteTitle(e.target.value)}
                  maxLength={80}
                  style={{marginBottom: 8, fontWeight: 600, fontSize: '1.1rem'}}
                />
                <textarea
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Write your note here..."
                  className="note-textarea"
                />
                <div className="button-group">
                  <button className="save-btn" onClick={saveNote}>
                    💾 Save Note
                  </button>
                  <button className="upload-btn" onClick={openFileInput}>
                    📎 Attach Files
                  </button>
                  <button 
                    className="upload-btn" 
                    onClick={handleCameraCapture}
                  >
                    📸 Camera Upload
                  </button>
                  <button
                    className={`upload-btn${isListening ? ' listening' : ''}`}
                    onClick={handleSpeechClick}
                    type="button"
                    style={isListening ? {background: 'linear-gradient(135deg, #ff7675 0%, #fd79a8 100%)'} : {}}
                  >
                    {isListening ? '🎤 Listening...' : '🎤 Speech'}
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept="image/*,.pdf"
                    onChange={handleFileInputChange}
                    style={{ display: 'none' }}
                  />
                </div>
                {isUploading && (
                  <div className="upload-progress">
                    <div className="progress-bar">
                      <div 
                        className="progress-fill" 
                        style={{ width: `${uploadProgress}%` }}
                      ></div>
                    </div>
                    <p>Uploading... {uploadProgress}%</p>
                  </div>
                )}
              </div>
              {isCameraActive && (
                <div className="camera-preview">
                  <video ref={videoRef} autoPlay playsInline muted />
                  <button 
                    className="capture-btn"
                    onClick={capturePhoto}
                  >
                    📸 Capture Photo
                  </button>
                </div>
              )}
              <div 
                className="drop-zone"
                onDrop={handleDrop}
                onDragOver={handleDragOver}
              >
                <p>📁 Drag and drop files here or click "Upload File"</p>
                <p className="drop-zone-hint">Supports: Images (JPEG, PNG, GIF, WebP) and PDFs (max 10MB)</p>
              </div>
              {/* Real-time AI Generation Display */}
              {isStreaming && (
                <div className="streaming-section">
                  <h2>🤖 AI is Enhancing Your Note...</h2>
                  <div className="streaming-content">
                    <div className="streaming-text" dangerouslySetInnerHTML={{ __html: generatedText.replace(/\n/g, '<br/>') }} />
                    <div className="streaming-cursor">|</div>
                  </div>
                </div>
              )}
              {/* AI Enhancement Comparison */}
              {showComparison && enhancedNote && (
                <div className="comparison-section">
                  <h2>🤖 AI Enhancement</h2>
                  <div className="comparison-container">
                    <div className="original-note">
                      <h3>�� Original Note</h3>
                      <div className="note-content">
                        {enhancedNote.original.type === 'file' ? (
                          <div className="file-note">
                            <div className="file-icon">
                              {enhancedNote.original.fileType.startsWith('image/') ? '🖼️' : '📄'}
                            </div>
                            <div className="file-info">
                              <div className="file-name">{enhancedNote.original.fileName}</div>
                              <div className="file-details">
                                {enhancedNote.original.fileType} • {(enhancedNote.original.fileSize / 1024 / 1024).toFixed(2)} MB
                              </div>
                            </div>
                          </div>
                        ) : (
                          <p>{enhancedNote.original.text}</p>
                        )}
                      </div>
                    </div>
                    <div className="enhanced-note">
                      <h3>✨ Enhanced Note</h3>
                      <div className="note-content">
                        <div className="enhanced-text" dangerouslySetInnerHTML={{ __html: enhancedNote.enhanced.replace(/\n/g, '<br/>') }} />
                      </div>
                    </div>
                  </div>
                  <div className="comparison-actions">
                    <button className="save-enhanced-btn" onClick={saveEnhancedNote}>
                      💾 Save Enhanced Note
                    </button>
                    <button className="close-comparison-btn" onClick={() => setShowComparison(false)}>
                      ❌ Close
                    </button>
                  </div>
                </div>
              )}
              {/* Recently Created Notes - Original Design */}
              <div className="notes-section">
                <h2>Recently Created</h2>
                <div className="notes-list">
                  {getFilteredNotes().slice(0, 10).length === 0 ? (
                    <div className="empty-state">
                      <p>📝 No notes yet</p>
                      <p>Add your first note above!</p>
                    </div>
                  ) : (
                    getFilteredNotes().slice(0, 10).map((note, index) => (
                      <React.Fragment key={note.id || note._id}>
                        <div
                          className="note-item"
                          draggable
                          onDragStart={() => handleNoteDragStart(note, null)}
                          onDragEnd={handleNoteDragEnd}
                        >
                          {editingNoteId === (note.id || note._id) ? (
                          /* Edit Mode */
                          <div className="note-edit-form">
                            <input
                              type="text"
                              value={editingNoteData.title}
                              onChange={(e) => setEditingNoteData({...editingNoteData, title: e.target.value})}
                              className="edit-title-input"
                              placeholder="Note title..."
                            />
                            <textarea
                              value={editingNoteData.text}
                              onChange={(e) => setEditingNoteData({...editingNoteData, text: e.target.value})}
                              className="edit-text-area"
                              placeholder="Note content..."
                              rows="4"
                            />
                            <div className="edit-actions">
                              <button onClick={handleSaveEdit} className="save-edit-btn">💾 Save</button>
                              <button onClick={handleCancelEdit} className="cancel-edit-btn">❌ Cancel</button>
                            </div>
                          </div>
                        ) : (
                          /* View Mode - Original Design */
                          <>
                            <div className="note-actions-header">
                              <button 
                                className="delete-btn" 
                                onClick={() => deleteNote(note.id || note._id)}
                                title="Delete note"
                              >
                                ×
                              </button>
                              <button 
                                className="edit-note-btn"
                                onClick={() => handleEditNote(note)}
                                title="Edit note"
                              >
                                ✏️
                              </button>
                            </div>
                            {note.isEnhanced && (
                              <div className="enhanced-badge">✨ AI Enhanced</div>
                            )}
                            {note.title && <div className="note-title" style={{fontWeight: 700, fontSize: '1.1rem', marginBottom: 4}}>{note.title}</div>}
                            {note.type === 'file' ? (
                              <div className="file-note" onClick={() => openFile(note)}>
                                <div className="file-icon">
                                  {note.fileType.startsWith('image/') ? '🖼️' : '📄'}
                                </div>
                                <div className="file-info">
                                  <div className="file-name">{note.fileName}</div>
                                  <div className="file-details">
                                    {note.fileType} • {(note.fileSize / 1024 / 1024).toFixed(2)} MB
                                  </div>
                                </div>
                              </div>
                            ) : (
                              renderNoteContent(note)
                            )}
                            <div className="note-actions">
                              <button 
                                className="enhance-btn" 
                                onClick={() => enhanceNoteWithAI(note)}
                                disabled={isEnhancing}
                              >
                                {isEnhancing ? '🤖 Enhancing...' : '🤖 Enhance with AI'}
                              </button>
                            </div>
                            <div className="note-date">{note.date}</div>
                          </>
                          )}
                        </div>

                        {/* Show banner ad after every 3 notes for free users */}
                        {!subscriptionInfo?.isPro && (index + 1) % 3 === 0 && index < 9 && (
                          <AdBanner 
                            subscriptionInfo={subscriptionInfo}
                            adType="banner"
                            showUpgradePrompt={true}
                            onUpgradeClick={() => setShowUpgradeModal(true)}
                            className="notes-list-ad"
                          />
                        )}
                      </React.Fragment>
                    ))
                  )}
                </div>
              </div>
            </>
          )}
          {activeView === 'storage' && (
            <div className="storage-view">
              <div className="folder-actions">
                <button className="create-folder-btn" onClick={handleCreateFolder}>
                  📂 New Folder
                </button>
                <button className="upload-btn" onClick={openFileInput}>
                  📎 Attach Files
                </button>
                <button 
                  className="upload-btn" 
                  onClick={handleCameraCapture}
                >
                  📸 Camera Upload
                </button>
                {/* Hidden file input for storage tab */}
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*,.pdf"
                  onChange={handleFileInputChange}
                  style={{ display: 'none' }}
                />
              </div>
              {!openedFolderId && !openedNoteId ? (
                <>
                  {/* Notes not in any folder, as cards */}
                  {notesNotInFolders.length > 0 && (
                    <div className="note-card-grid">
                      {notesNotInFolders.map(note => (
                        <div
                          key={note.id || note._id}
                          className="folder-card note-card"
                          draggable
                          onDragStart={() => handleNoteDragStart(note, null)}
                          onDragEnd={handleNoteDragEnd}
                          onClick={() => handleOpenNote(note.id || note._id)}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="folder-icon">📝</div>
                          <div className="folder-info">
                            <div className="folder-name" style={{fontWeight: 700, fontSize: '1.1rem', marginBottom: 6, minHeight: 32}}>
                              {note.title || 'Untitled Note'}
                            </div>
                            <div className="note-preview" style={{fontSize: '0.9rem', color: '#666', marginBottom: 4, lineHeight: 1.3}}>
                              {getCleanTextPreview(note.text).split('\n')[0].substring(0, 60)}{getCleanTextPreview(note.text).length > 60 ? '...' : ''}
                            </div>
                            {note.isEnhanced && (
                              <span className="enhanced-badge" style={{fontSize: '0.7rem', marginBottom: '0.5rem'}}>🤖 AI Enhanced</span>
                            )}
                            <div className="storage-note-actions">
                              <button 
                                className="enhance-btn-small" 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  enhanceNoteWithAI(note);
                                }}
                                disabled={isEnhancing}
                                title="Enhance with AI"
                              >
                                🤖
                              </button>
                              <button 
                                className="edit-note-btn-small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleStorageEditNote(note);
                                }}
                                title="Edit note"
                              >
                                ✏️
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="folder-grid">
                    {draggedNote && draggedNote.fromFolderId && (
                      <div
                        className={`folder-card storage-drop-zone${noteDropZoneActive ? ' active' : ''}`}
                        onDragOver={handleStorageHomeDragOver}
                        onDragLeave={handleStorageHomeDragLeave}
                        onDrop={handleStorageHomeDrop}
                        style={{ minWidth: 180, minHeight: 140, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      >
                        <span>⬆️ Drop notes here to move out of folder</span>
                      </div>
                    )}
                    {filteredFolders.map(folder => (
                      <div
                        key={folder.id}
                        className={`folder-card${dragOverFolderId === folder.id ? ' drag-over' : ''}${folderDragOverId === folder.id ? ' folder-drag-over' : ''}`}
                        onClick={e => handleFolderCardClick(e, folder.id)}
                        onDragOver={e => { handleFolderDragOver(e, folder.id); handleFolderCardDragOver(e, folder.id); }}
                        onDragLeave={e => { handleFolderDragLeave(); setFolderDragOverId(null); }}
                        onDrop={e => { handleFolderDrop(e, folder.id); handleFolderCardDrop(e, folder.id); }}
                        draggable={folder.id !== 'default'}
                        onDragStart={folder.id !== 'default' ? () => handleFolderDragStart(folder.id) : undefined}
                        onDragEnd={folder.id !== 'default' ? handleFolderDragEnd : undefined}
                        style={{ position: 'relative' }}
                      >
                        {/* Delete folder button in top-right corner */}
                        {folder.id !== 'default' && (
                          <button
                            className="delete-btn folder-delete-btn"
                            onClick={e => { e.stopPropagation(); handleDeleteFolder(folder.id); }}
                            title="Delete folder"
                            style={{ position: 'absolute', top: 8, right: 8, zIndex: 2 }}
                          >
                            ×
                          </button>
                        )}
                        <div className="folder-icon">📁</div>
                        <div className="folder-info">
                          {renamingFolderId === folder.id ? null : (
                            <div className="folder-name-row">
                              <div className="folder-name">{folder.name}</div>
                              <button className="rename-folder-btn" onClick={e => { e.stopPropagation(); handleRenameFolder(folder.id, folder.name); }} title="Rename folder">✏️</button>
                            </div>
                          )}
                          <div className="note-count">{notes.filter(note => note.folderId === folder.id).length} notes</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : openedNoteId ? (
                <div className="opened-note-view">
                  <div className="opened-folder-header">
                    <button className="back-btn" onClick={handleCloseNote}>← Back</button>
                    <span className="opened-folder-title">{notes.find(n => (n.id || n._id) === openedNoteId)?.title || 'Note'}</span>
                  </div>
                  <div className="note-detail-content">
                    {(() => {
                      const note = notes.find(n => (n.id || n._id) === openedNoteId);
                      if (!note) return <div>Note not found</div>;
                      
                      return (
                        <div className="note-item">
                          {note.isEnhanced && (
                            <div className="enhanced-badge">✨ AI Enhanced</div>
                          )}
                          {note.title && <div className="note-title">{note.title}</div>}
                          {note.type === 'file' ? (
                            <div className="file-note" onClick={() => openFile(note)}>
                              <div className="file-icon">
                                {note.fileType.startsWith('image/') ? '🖼️' : '📄'}
                              </div>
                              <div className="file-info">
                                <div className="file-name">{note.fileName}</div>
                                <div className="file-details">
                                  {note.fileType} • {(note.fileSize / 1024 / 1024).toFixed(2)} MB
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="note-text" dangerouslySetInnerHTML={{ __html: note.text.replace(/\n/g, '<br/>') }} />
                          )}
                          <div className="note-actions">
                            <button 
                              className="enhance-btn" 
                              onClick={() => enhanceNoteWithAI(note)}
                              disabled={isEnhancing}
                            >
                              {isEnhancing ? '🤖 Enhancing...' : '🤖 Enhance with AI'}
                            </button>
                            <button 
                              className="delete-btn" 
                              onClick={() => { deleteNote(note.id); handleCloseNote(); }}
                              title="Delete note"
                            >
                              ×
                            </button>
                          </div>
                          <div className="note-date">{note.date}</div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              ) : (
                <div className="opened-folder-view">
                  <div className="opened-folder-header">
                    <button className="back-btn" onClick={handleCloseFolder}>← Back</button>
                    <span className="opened-folder-title">{folders.find(f => f.id === openedFolderId)?.name}</span>
                  </div>
                  <div className="notes-list">
                    {(() => {
                      const folderNotes = notes.filter(note => note.folderId === openedFolderId);
                      return folderNotes.length === 0 ? (
                        <div className="empty-state">
                          <p>📝 No notes in this folder</p>
                          <p>Drag notes here or add new notes from the main view!</p>
                        </div>
                      ) : (
                        folderNotes.map(note => (
                          <div
                            key={note.id || note._id}
                            className="note-item"
                            draggable
                            onDragStart={() => handleNoteDragStart(note, openedFolderId)}
                            onDragEnd={handleNoteDragEnd}
                          >
                            {editingNoteId === (note.id || note._id) ? (
                              /* Edit Mode for folder notes */
                              <div className="note-edit-form">
                                <input
                                  type="text"
                                  value={editingNoteData.title}
                                  onChange={(e) => setEditingNoteData({...editingNoteData, title: e.target.value})}
                                  className="edit-title-input"
                                  placeholder="Note title..."
                                />
                                <textarea
                                  value={editingNoteData.text}
                                  onChange={(e) => setEditingNoteData({...editingNoteData, text: e.target.value})}
                                  className="edit-text-area"
                                  placeholder="Note content..."
                                  rows="4"
                                />
                                <div className="edit-actions">
                                  <button onClick={handleSaveEdit} className="save-edit-btn">💾 Save</button>
                                  <button onClick={handleCancelEdit} className="cancel-edit-btn">❌ Cancel</button>
                                </div>
                              </div>
                            ) : (
                              /* View Mode for folder notes */
                              <>
                                <div className="note-actions-header">
                                  <button 
                                    className="delete-btn" 
                                    onClick={() => deleteNote(note.id || note._id)}
                                    title="Delete note"
                                  >
                                    ×
                                  </button>
                                  <button 
                                    className="edit-note-btn"
                                    onClick={() => handleEditNote(note)}
                                    title="Edit note"
                                  >
                                    ✏️
                                  </button>
                                </div>
                                {note.isEnhanced && (
                                  <div className="enhanced-badge">✨ AI Enhanced</div>
                                )}
                                {note.title && <div className="note-title" style={{fontWeight: 700, fontSize: '1.1rem', marginBottom: 4}}>{note.title}</div>}
                                {note.type === 'file' ? (
                                  <div className="file-note" onClick={() => openFile(note)}>
                                    <div className="file-icon">
                                      {note.fileType.startsWith('image/') ? '🖼️' : '📄'}
                                    </div>
                                    <div className="file-info">
                                      <div className="file-name">{note.fileName}</div>
                                      <div className="file-details">
                                        {note.fileType} • {(note.fileSize / 1024 / 1024).toFixed(2)} MB
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  renderNoteContent(note)
                                )}
                                <div className="note-actions">
                                  <button 
                                    className="enhance-btn" 
                                    onClick={() => enhanceNoteWithAI(note)}
                                    disabled={isEnhancing}
                                  >
                                    {isEnhancing ? '🤖 Enhancing...' : '🤖 Enhance with AI'}
                                  </button>
                                </div>
                                <div className="note-date">{note.date}</div>
                              </>
                            )}
                          </div>
                        ))
                      );
                    })()}
                    {/* Drop zone at the bottom for moving notes out of folder */}
                    {draggedNote && draggedNote.fromFolderId === openedFolderId && (
                      <div
                        className={`folder-card storage-drop-zone${noteDropZoneActive ? ' active' : ''}`}
                        onDragOver={handleStorageHomeDragOver}
                        onDragLeave={handleStorageHomeDragLeave}
                        onDrop={handleStorageHomeDrop}
                        style={{ minWidth: 180, minHeight: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 24 }}
                      >
                        <span>⬆️ Drop here to move note out of this folder</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {/* Folder Rename Modal */}
      {renamingFolderId && (
        <div className="modal-overlay">
          <div className="modal-card">
            <h3 className="modal-title">Rename Folder</h3>
            <input
              className="modal-input"
              value={newFolderName}
              autoFocus
              maxLength={40}
              onChange={e => setNewFolderName(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && newFolderName.trim()) handleSaveFolderName(renamingFolderId);
                if (e.key === 'Escape') handleCancelRename();
              }}
            />
            <div className="modal-actions">
              <button
                className="modal-btn save"
                onClick={() => handleSaveFolderName(renamingFolderId)}
                disabled={!newFolderName.trim()}
              >
                Save
              </button>
              <button
                className="modal-btn cancel"
                onClick={handleCancelRename}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Auth Modal */}
      <AuthModal 
        isOpen={showAuthModal} 
        onClose={() => setShowAuthModal(false)} 
        onAuthSuccess={handleAuthSuccess} 
      />

      {/* Storage Edit Modal */}
      {showStorageEditModal && (
        <div className="modal-overlay" onClick={handleStorageEditCancel}>
          <div className="storage-edit-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Note</h3>
              <button className="modal-close-btn" onClick={handleStorageEditCancel}>×</button>
            </div>
            <div className="modal-content">
              <div className="form-group">
                <label htmlFor="edit-title">Title</label>
                <input
                  id="edit-title"
                  type="text"
                  value={editingNoteData.title}
                  onChange={(e) => setEditingNoteData({...editingNoteData, title: e.target.value})}
                  className="modal-title-input"
                  placeholder="Note title..."
                />
              </div>
              <div className="form-group">
                <label htmlFor="edit-content">Content</label>
                <textarea
                  id="edit-content"
                  value={editingNoteData.text}
                  onChange={(e) => setEditingNoteData({...editingNoteData, text: e.target.value})}
                  className="modal-text-area"
                  placeholder="Note content..."
                  rows="8"
                />
              </div>
            </div>
            <div className="modal-actions">
              <button onClick={handleStorageEditSave} className="modal-save-btn">
                💾 Save Changes
              </button>
              <button onClick={handleStorageEditCancel} className="modal-cancel-btn">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade Modal */}
      <UpgradeModal 
        isOpen={showUpgradeModal} 
        onClose={() => setShowUpgradeModal(false)} 
        subscriptionInfo={subscriptionInfo}
        user={user}
        onRefreshSubscription={syncDataFromCloud}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showConfirmationModal}
        onClose={() => setShowConfirmationModal(false)}
        title={confirmationConfig.title}
        message={confirmationConfig.message}
        confirmText={confirmationConfig.confirmText}
        cancelText={confirmationConfig.cancelText}
        confirmButtonClass={confirmationConfig.confirmButtonClass}
        onConfirm={confirmationConfig.onConfirm}
      />

      {/* Enhancement Ad Modal */}
      {showEnhancementAd && (
        <AdBanner
          subscriptionInfo={subscriptionInfo}
          adType="enhancement-interstitial"
          showUpgradePrompt={true}
          onUpgradeClick={handleUpgradeFromAd}
        />
      )}
    </div>
  );
}

export default App;
